// Permanent debug screen — only mounted on dev / staging / local hosts.
// Captures console errors, window errors, unhandled rejections and the
// last few app-state snapshots into a ring buffer. Tap the bug FAB or
// 5-tap the version label in Settings to open. Send a screenshot of
// this screen to debug an issue without explaining anything.

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

const MAX_ENTRIES = 80;
const _entries = [];
const _state = {};
let _installed = false;

function fmt(arg) {
  if (arg === null || arg === undefined) return String(arg);
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? "\n  " + arg.stack.split("\n").slice(1,3).join("\n  ") : ""}`;
  try { return JSON.stringify(arg); } catch { return String(arg); }
}

function push(level, msg) {
  _entries.push({ t: Date.now(), level, msg: String(msg).slice(0, 600) });
  if (_entries.length > MAX_ENTRIES) _entries.shift();
}

// Install global capture once, on first import.
function install() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  const origError = console.error.bind(console);
  const origWarn  = console.warn.bind(console);
  console.error = (...args) => { push("error", args.map(fmt).join(" ")); origError(...args); };
  console.warn  = (...args) => { push("warn",  args.map(fmt).join(" ")); origWarn(...args); };
  window.addEventListener("error", (e) => {
    push("error", `${e.message || "Error"} @ ${(e.filename||"").split("/").pop()}:${e.lineno||""}`);
  });
  window.addEventListener("unhandledrejection", (e) => {
    push("error", `Unhandled rejection: ${fmt(e.reason)}`);
  });
}
install();

export function setDebugState(key, value) {
  _state[key] = value;
}

function gather() {
  const safe = (fn, fb = null) => { try { return fn(); } catch { return fb; } };
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  return {
    when: new Date().toISOString(),
    location: {
      host: safe(() => window.location.hostname, ""),
      path: safe(() => window.location.pathname + window.location.search, ""),
    },
    build: {
      buildTime: process.env.REACT_APP_BUILD_TIME || "unknown",
      env: process.env.NODE_ENV,
      branch: process.env.REACT_APP_BRANCH || "unknown",
    },
    platform: {
      capacitor: !!(cap?.isNativePlatform?.() || cap?.isNative),
      capPlatform: cap?.getPlatform?.() || "web",
      plugins: cap?.Plugins ? Object.keys(cap.Plugins) : [],
      ua: safe(() => navigator.userAgent, ""),
      lang: safe(() => navigator.language, ""),
      online: safe(() => navigator.onLine, true),
      viewport: safe(() => `${window.innerWidth}×${window.innerHeight}`, ""),
      pixelRatio: safe(() => window.devicePixelRatio, 1),
    },
    nav: {
      lastTab: safe(() => localStorage.getItem("lastTab"), null),
      lastToolView: safe(() => localStorage.getItem("lastToolView"), null),
      lastHayahFeature: safe(() => localStorage.getItem("lastHayahFeature"), null),
      lastTabAt: safe(() => {
        const v = parseInt(localStorage.getItem("lastTabAt") || "0", 10);
        return v ? new Date(v).toLocaleString() : null;
      }, null),
    },
    state: { ..._state },
    storage: {
      count: safe(() => Object.keys(localStorage).length, 0),
      bytes: safe(() => Object.keys(localStorage).reduce((s, k) => s + (localStorage.getItem(k)?.length || 0), 0), 0),
      keys: safe(() => Object.keys(localStorage).sort(), []),
    },
    logs: _entries.slice().reverse(),
  };
}

// ── Detect non-prod host so we only render the FAB there ─────────────
export function isDebugHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  if (h.includes("localhost") || h.includes("127.0.0.1")) return true;
  if (h.includes("pages.dev")) return true; // Cloudflare preview
  if (localStorage.getItem("sirat_debug") === "1") return true;
  return false;
}

// ── Floating button ──────────────────────────────────────────────────
export function DebugFAB({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Open debug screen"
      style={{
        position: "fixed",
        left: 10,
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 4px)",
        width: 28, height: 28, borderRadius: 14,
        background: "rgba(255,179,0,0.55)", color: "#1a0f00",
        border: "1px solid rgba(255,179,0,0.7)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
        zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", padding: 0, fontFamily: "inherit",
        fontSize: 13, lineHeight: 1, fontWeight: 900, opacity: 0.85,
      }}
    >🐛</button>
  );
}

// ── Full-screen overlay ──────────────────────────────────────────────
export function DebugScreen({ open, onClose }) {
  const [info, setInfo] = useState(null);
  const [copyState, setCopyState] = useState(null); // "ok" | "err" | null

  useEffect(() => {
    if (!open) return;
    setInfo(gather());
    const i = setInterval(() => setInfo(gather()), 1500);
    return () => clearInterval(i);
  }, [open]);

  if (!open || !info) return null;

  const dump = JSON.stringify(info, null, 2);

  const copyDump = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(dump);
      ok = true;
    } catch {
      // clipboard API requires HTTPS — fallback for LAN dev server (HTTP)
      try {
        const ta = Object.assign(document.createElement("textarea"), {
          value: dump, style: "position:fixed;top:-999px;left:-999px;opacity:0",
        });
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {}
    }
    setCopyState(ok ? "ok" : "err");
    setTimeout(() => setCopyState(null), 1500);
  };

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 100000,
      background: "#07090c", color: "#e8e3d3",
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 11, lineHeight: 1.55,
      paddingTop: "env(safe-area-inset-top, 0px)",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #1f1f25", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#ffb300", letterSpacing: "0.05em" }}>🐛 DEBUG</div>
          <div style={{ fontSize: 9, color: "#7a766b", marginTop: 2 }}>{info.when}</div>
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          <button onClick={copyDump} style={hdrBtn(copyState === "ok" ? "rgba(124,196,160,0.25)" : "rgba(255,179,0,0.15)", copyState === "ok" ? "#7CC4A0" : "#ffb300")}>
            {copyState === "ok" ? "✓ Copied" : copyState === "err" ? "Fail" : "Copy"}
          </button>
          <button onClick={onClose} style={hdrBtn("rgba(255,255,255,0.08)", "#c6bfa6")}>Done</button>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px 28px" }}>
        <Section title="BUILD / DEPLOY">
          <Row k="host" v={info.location.host} />
          <Row k="path" v={info.location.path} />
          <Row k="branch" v={info.build.branch} />
          <Row k="buildTime" v={info.build.buildTime} />
          <Row k="env" v={info.build.env} />
        </Section>

        <Section title="PLATFORM">
          <Row k="runtime" v={info.platform.capacitor ? `native · ${info.platform.capPlatform}` : "web"} />
          <Row k="plugins" v={info.platform.plugins.join(", ") || "—"} />
          <Row k="viewport" v={`${info.platform.viewport} · DPR ${info.platform.pixelRatio}`} />
          <Row k="online" v={String(info.platform.online)} />
          <Row k="lang" v={info.platform.lang} />
          <Row k="UA" v={info.platform.ua} multi />
        </Section>

        <Section title="NAVIGATION">
          <Row k="tab" v={info.nav.lastTab || "—"} />
          <Row k="toolView" v={info.nav.lastToolView || "—"} />
          <Row k="hayahFeature" v={info.nav.lastHayahFeature || "—"} />
          <Row k="lastTabAt" v={info.nav.lastTabAt || "—"} />
        </Section>

        {Object.keys(info.state).length > 0 && (
          <Section title="APP STATE">
            {Object.entries(info.state).map(([k, v]) => (
              <Row key={k} k={k} v={String(v)} />
            ))}
          </Section>
        )}

        <Section title={`STORAGE · ${info.storage.count} keys · ${(info.storage.bytes / 1024).toFixed(1)} KB`}>
          <div style={{ fontSize: 9.5, color: "#7a766b", wordBreak: "break-all", lineHeight: 1.7 }}>
            {info.storage.keys.join(" · ")}
          </div>
        </Section>

        <Section title={`LOGS · ${info.logs.length}`}>
          {info.logs.length === 0 ? (
            <div style={{ color: "#7a766b", padding: "4px 0" }}>No errors or warnings captured</div>
          ) : info.logs.map((log, i) => (
            <div key={i} style={{
              padding: "7px 0", borderBottom: "1px solid #1a1a1e",
              color: log.level === "error" ? "#f08a6b" : log.level === "warn" ? "#ffb300" : "#c6bfa6",
            }}>
              <div style={{ fontSize: 9, color: "#7a766b", marginBottom: 2 }}>
                {new Date(log.t).toLocaleTimeString()} · {log.level}
              </div>
              <div style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{log.msg}</div>
            </div>
          ))}
        </Section>
      </div>
    </div>,
    document.body
  );
}

const hdrBtn = (bg, color) => ({
  padding: "7px 12px", borderRadius: 9, background: bg,
  border: `1px solid ${color}55`, color, fontSize: 11, fontWeight: 700,
  cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em",
});

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: "#7a766b", letterSpacing: "0.15em", marginBottom: 6 }}>{title}</div>
      <div style={{ background: "#0e0e10", border: "1px solid #1a1a1e", borderRadius: 10, padding: "8px 12px" }}>
        {children}
      </div>
    </div>
  );
}

function Row({ k, v, multi }) {
  return (
    <div style={{ display: "flex", flexDirection: multi ? "column" : "row", gap: multi ? 2 : 8, padding: "3px 0" }}>
      <div style={{ color: "#7a766b", minWidth: 78, flexShrink: 0 }}>{k}</div>
      <div style={{ flex: 1, color: "#e8e3d3", wordBreak: "break-all" }}>{v}</div>
    </div>
  );
}
